// gioco/fotoPartita.ts — v040: le foto della partita. Durante il round il telefono scatta qualche foto (poco dopo il
// VIA, ogni tanto durante il gioco e nel momento della risata). Alla fine chi gioca sceglie quali condividere, già nella
// cornice di Laughdown (cartaMeme). Le foto restano nella memoria di questa pagina: non vanno al server.
//
// - La mia parte viene dalla FOTOCAMERA (senza filtro): prima la foto poteva essere solo la faccina del filtro.
// - L'altro c'è solo se ha detto sì alle foto (vista.foto); altrimenti la foto ha solo me.
// - Se io non ho detto sì alle foto, non si scatta niente.
import { useEffect, useMemo, useRef, useState, type RefObject } from "react";
import type { Fase } from "../../../condiviso/partita.ts";

export type MomentoFoto = "inizio" | "gioco" | "risata";

export interface FotoPartita {
  id: string;
  momento: MomentoFoto;
  /** la mia faccia (fotocamera, specchiata come un selfie), data URL JPEG */
  io: string | null;
  /** la sua faccia (video ricevuto), data URL JPEG; null se non c'è o non ha detto sì */
  lui: string | null;
  /** v042: partita a 4 — le quattro facce (p1…p4; null = posto vuoto o chi non ha detto sì) */
  griglia?: (string | null)[];
}

const LATO = 540;
const PRIMA_DOPO_VIA_MS = 2500;
const OGNI_MS = 7000;
const MASSIMO_GIOCO = 2; // foto «durante il gioco» tenute (le ultime)

/** Un fotogramma del video come JPEG (data URL), ritagliato al centro in un rettangolo w×h. */
export function scattoVideo(v: HTMLVideoElement | null, specchio: boolean, w = LATO, h = LATO): string | null {
  if (!v || v.readyState < 2 || !v.videoWidth) return null;
  try {
    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;
    const x = c.getContext("2d");
    if (!x) return null;
    const k = Math.max(w / v.videoWidth, h / v.videoHeight);
    const sw = w / k;
    const sh = h / k;
    if (specchio) {
      x.translate(w, 0);
      x.scale(-1, 1);
    }
    x.drawImage(v, (v.videoWidth - sw) / 2, (v.videoHeight - sh) / 2, sw, sh, 0, 0, w, h);
    return c.toDataURL("image/jpeg", 0.86);
  } catch {
    return null;
  }
}

function carica(src: string): Promise<HTMLImageElement | null> {
  return new Promise((ok) => {
    const i = new Image();
    i.onload = () => ok(i);
    i.onerror = () => ok(null);
    i.src = src;
  });
}

/** v042: le quattro facce della partita a 4 in un quadrato 2×2 (chi non c'è: riquadro scuro con il «?»). */
async function componiGriglia(celle: (string | null)[]): Promise<string | null> {
  const img = await Promise.all(celle.map((c) => (c ? carica(c) : Promise.resolve(null))));
  const c = document.createElement("canvas");
  c.width = LATO;
  c.height = LATO;
  const x = c.getContext("2d");
  if (!x) return null;
  const m = LATO / 2;
  for (let k = 0; k < 4; k++) {
    const dx = (k % 2) * m;
    const dy = Math.floor(k / 2) * m;
    const i = img[k];
    if (i) {
      const s = Math.max(m / i.naturalWidth, m / i.naturalHeight);
      const sw = m / s;
      const sh = m / s;
      x.drawImage(i, (i.naturalWidth - sw) / 2, (i.naturalHeight - sh) / 2, sw, sh, dx, dy, m, m);
    } else {
      x.fillStyle = "#17113a";
      x.fillRect(dx, dy, m, m);
      x.fillStyle = "rgb(255 255 255 / 50%)";
      x.font = `900 ${Math.round(m * 0.42)}px system-ui, sans-serif`;
      x.textAlign = "center";
      x.textBaseline = "middle";
      x.fillText("?", dx + m / 2, dy + m / 2);
    }
  }
  x.fillStyle = "#ffffff";
  x.fillRect(m - 3, 0, 6, LATO);
  x.fillRect(0, m - 3, LATO, 6);
  return c.toDataURL("image/jpeg", 0.86);
}

/** Le due facce affiancate in un quadrato (lui a sinistra, io a destra), o solo la mia. v042: nella partita a 4, le quattro. */
export async function componiFoto(f: FotoPartita, conLui: boolean): Promise<string | null> {
  if (conLui && f.griglia) return componiGriglia(f.griglia);
  const parti = (conLui && f.lui ? [f.lui, f.io] : [f.io]).filter((x): x is string => !!x);
  if (parti.length < 2) return parti[0] ?? null;
  const img = (await Promise.all(parti.map(carica))).filter((i): i is HTMLImageElement => i !== null);
  if (img.length < 2) return f.io ?? f.lui;
  const c = document.createElement("canvas");
  c.width = LATO;
  c.height = LATO;
  const x = c.getContext("2d");
  if (!x) return null;
  const meta = LATO / 2;
  img.forEach((i, n) => {
    const k = Math.max(meta / i.naturalWidth, LATO / i.naturalHeight);
    const sw = meta / k;
    const sh = LATO / k;
    x.drawImage(i, (i.naturalWidth - sw) / 2, (i.naturalHeight - sh) / 2, sw, sh, n * meta, 0, meta, LATO);
  });
  x.fillStyle = "#ffffff";
  x.fillRect(meta - 3, 0, 6, LATO);
  return c.toDataURL("image/jpeg", 0.86);
}

/**
 * Scatta le foto del round. `camera` è la fotocamera (senza filtro), `videoLui` il <video> dell'altro.
 * Si riparte da zero a ogni nuovo conto alla rovescia.
 */
export function useFotoPartita(p: { fase: Fase | null; camera: MediaStream; videoLui: RefObject<HTMLVideoElement | null>; mio: boolean; suo: boolean }): FotoPartita[] {
  const [foto, setFoto] = useState<FotoPartita[]>([]);
  const videoCamera = useRef<HTMLVideoElement | null>(null);
  const suoRif = useRef(p.suo);
  suoRif.current = p.suo;
  const mioRif = useRef(p.mio);
  mioRif.current = p.mio;

  // un <video> nascosto con la fotocamera: le foto non hanno il filtro
  useEffect(() => {
    const v = document.createElement("video");
    v.muted = true;
    v.playsInline = true;
    v.setAttribute("playsinline", "");
    v.srcObject = new MediaStream(p.camera.getVideoTracks());
    void v.play().catch(() => undefined);
    videoCamera.current = v;
    return () => {
      v.pause();
      v.srcObject = null; // le tracce restano dell'app
      videoCamera.current = null;
    };
  }, [p.camera]);

  const scatta = (momento: MomentoFoto) => {
    if (!mioRif.current) return;
    const io = scattoVideo(videoCamera.current, true);
    const lui = suoRif.current ? scattoVideo(p.videoLui.current, false) : null;
    if (!io && !lui) return;
    const nuova: FotoPartita = { id: `${momento}-${Date.now()}`, momento, io, lui };
    setFoto((prima) => {
      if (momento === "inizio" && prima.some((f) => f.momento === "inizio")) return prima; // dopo una pausa: si tiene la prima
      if (momento !== "gioco") return [...prima.filter((f) => f.momento !== momento), nuova];
      const gioco = [...prima.filter((f) => f.momento === "gioco"), nuova].slice(-MASSIMO_GIOCO);
      return [...prima.filter((f) => f.momento !== "gioco"), ...gioco];
    });
  };
  const scattaRif = useRef(scatta);
  scattaRif.current = scatta;

  const fasePrima = useRef(p.fase);
  useEffect(() => {
    const prima = fasePrima.current;
    fasePrima.current = p.fase;
    if (p.fase === "conto") setFoto([]);
    if (prima === "gioco" && p.fase !== "gioco") scattaRif.current("risata");
    if (p.fase !== "gioco") return;
    const primo = setTimeout(() => scattaRif.current("inizio"), PRIMA_DOPO_VIA_MS);
    const ogni = setInterval(() => scattaRif.current("gioco"), OGNI_MS);
    return () => {
      clearTimeout(primo);
      clearInterval(ogni);
    };
  }, [p.fase]);

  // l'altro ritira il sì: le sue foto spariscono subito
  useEffect(() => {
    if (!p.suo) setFoto((prima) => (prima.some((f) => f.lui) ? prima.map((f) => ({ ...f, lui: null })) : prima));
  }, [p.suo]);
  // io ritiro il sì: niente foto
  useEffect(() => {
    if (!p.mio) setFoto([]);
  }, [p.mio]);

  // in ordine: inizio, gioco, risata (lo stesso elenco finché le foto non cambiano)
  return useMemo(() => {
    const ordine: Record<MomentoFoto, number> = { inizio: 0, gioco: 1, risata: 2 };
    return [...foto].sort((a, b) => ordine[a.momento] - ordine[b.momento]);
  }, [foto]);
}
